import { Fragment, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  ChevronDown,
  CopyPlus,
  Languages,
  Layers,
  MoreVertical,
  PencilLine,
  Search,
  Sparkles,
  Trash2,
  UserCheck,
  UserPlus,
  Users,
  UserX,
  type LucideIcon,
} from 'lucide-react';

import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Menu } from '@/components/ui/Menu';
import { Pagination } from '@/components/ui/Pagination';
import { ClassTeachingGrid } from '@/features/institute/staff/components/ClassTeachingGrid';
import { formatCurrency } from '@/lib/format';
import { cn, getNameInitials } from '@/lib/utils';
import type { PaginationMeta } from '@/types/api';
import type { ClassSection, ClassTeacher, InstituteClass } from '@/types/instituteClass';

import { STAGE_TONE, classDisplayName, stageOfClass, type ClassSectionRow } from '../../utils';

/** The register, which every seat figure on this screen leads to. */
const STUDENTS_PATH = '/admin/students';

/** How many rows the reader may choose between. */
export const ROWS_PER_PAGE = [10, 25, 50] as const;

/** Renders core subjects across max 2 lines */
/** How many subjects of one kind a row shows before folding the rest into "+N". */
const SUBJECTS_SHOWN = 3;

/** The three kinds of subject, in the order a timetable is read. */
const SUBJECT_GROUPS = [
  { key: 'coreSubjects', label: 'Core', icon: BookOpen, tone: 'success', text: 'text-ok' },
  { key: 'electives', label: 'Electives', icon: Sparkles, tone: 'warning', text: 'text-warn' },
  { key: 'languages', label: 'Languages', icon: Languages, tone: 'info', text: 'text-info' },
] as const satisfies readonly {
  key: 'coreSubjects' | 'electives' | 'languages';
  label: string;
  icon: LucideIcon;
  tone: BadgeTone;
  text: string;
}[];

type SubjectGroupKey = (typeof SUBJECT_GROUPS)[number]['key'];

/**
 * Every subject a class teaches, in one cell — core, electives and languages
 * each on a labelled line of their own.
 *
 * One column rather than three: most classes leave electives or languages
 * empty, and three mostly-blank columns pushed the fee and the teacher off
 * the screen. A kind with nothing in it is left out rather than shown as a dash.
 * Past `SUBJECTS_SHOWN` the rest fold into "+N"; pressing it opens that line
 * in place, and "Show less" folds it again.
 */
function SubjectsCell({ instituteClass }: { instituteClass: InstituteClass }) {
  const [openGroups, setOpenGroups] = useState<ReadonlySet<SubjectGroupKey>>(new Set());
  const groups = SUBJECT_GROUPS.filter((group) => instituteClass[group.key].length > 0);

  function toggle(key: SubjectGroupKey) {
    setOpenGroups((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  if (groups.length === 0) {
    return <span className="text-[11px] font-semibold text-slate-400">No subjects yet</span>;
  }

  return (
    <div className="flex min-w-[190px] max-w-[250px] flex-col gap-1.5">
      {groups.map(({ key, label, icon: Icon, tone, text }) => {
        const items = instituteClass[key];
        const isOpen = openGroups.has(key);
        const shown = isOpen ? items : items.slice(0, SUBJECTS_SHOWN);
        const hiddenCount = items.length - SUBJECTS_SHOWN;

        return (
          <div key={key} className="flex items-start gap-2">
            <span
              className={cn(
                'inline-flex w-[74px] shrink-0 items-center gap-1 pt-0.5 text-[10px] font-extrabold uppercase tracking-wide',
                text,
              )}
            >
              <Icon className="h-3 w-3 shrink-0" aria-hidden />
              {label}
            </span>
            <div className="flex min-w-0 flex-wrap gap-1">
              {shown.map((item, index) => (
                <Badge
                  key={`${item}-${index}`}
                  tone={tone}
                  className="font-sans text-[11.5px] font-bold tracking-normal whitespace-nowrap capitalize"
                >
                  {item}
                </Badge>
              ))}
              {hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={() => toggle(key)}
                  aria-expanded={isOpen}
                  aria-label={
                    isOpen
                      ? `Show fewer ${label.toLowerCase()}`
                      : `Show all ${items.length} ${label.toLowerCase()}`
                  }
                  className="cursor-pointer rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
                >
                  <Badge className="font-sans text-[11.5px] font-bold tracking-normal whitespace-nowrap hover:text-ink">
                    {isOpen ? 'Show less' : `+${hiddenCount}`}
                  </Badge>
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Who runs this section — face, name and address.
 *
 * One teacher per row, because one row is one section. Nothing is invented — a
 * section nobody has been allotted to says so, and the address is simply absent
 * for a reader the server withheld it from.
 */
function TeacherCell({ teacher }: { teacher: ClassTeacher | null }) {
  if (!teacher) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 dark:border-slate-700 px-2 py-0.5 text-xs font-medium text-slate-400">
        <UserX className="h-3.5 w-3.5 shrink-0" aria-hidden />
        Not allotted
      </span>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span
        className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-900/60 text-xs font-bold text-indigo-600 dark:text-indigo-400"
        aria-hidden
      >
        {teacher.avatarUrl ? (
          <img src={teacher.avatarUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          getNameInitials(teacher.fullName)
        )}
      </span>

      <span className="flex min-w-0 flex-col">
        <span className="truncate text-[13px] font-bold text-slate-800 dark:text-slate-100">{teacher.fullName}</span>
        <span className="truncate text-[11px] text-slate-500 dark:text-slate-400">
          {teacher.email ?? teacher.designation ?? 'Class teacher'}
        </span>
      </span>
    </div>
  );
}

export interface ClassesOverviewTableProps {
  /** Just this page of the roster, one row per section — the caller has already sliced it. */
  rows: ClassSectionRow[];
  meta: PaginationMeta;
  canManage: boolean;
  /** `ALLOCATION_MANAGE`, not `CLASS_MANAGE` — who runs a section is the staff
   *  room's arrangement, and the two keys are given out separately. */
  canAllotTeachers: boolean;
  /** `ALLOCATION_VIEW` — enough to read who teaches what, not to change it. */
  canViewAllocations: boolean;
  isLoading: boolean;
  /** Decides which of the two empty states the table shows. */
  isNarrowed: boolean;
  /** The row being duplicated, so its menu can say the request is in flight. */
  busyUuid: string | null;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onClearFilters: () => void;
  onAddClass: () => void;
  onEdit: (row: InstituteClass) => void;
  onDelete: (row: InstituteClass) => void;
  /** Removes one section of a class that has others; `onDelete` is for the last one. */
  onDeleteSection: (row: InstituteClass, section: ClassSection) => void;
  onDuplicate: (row: InstituteClass) => void;
  /** Opens the class-teacher picker for this class, on this screen. */
  onAssignTeacher: (row: InstituteClass) => void;
  /** Opens the section-by-section grid of who teaches what in this class. */
  onViewSubjectTeachers: (row: InstituteClass) => void;
  /** Opens the class monitor picker for one section. Only passed to those who may set it. */
  onSetMonitor?: (row: InstituteClass, section: ClassSection) => void;
}

export function ClassesOverviewTable({
  rows,
  meta,
  canManage,
  canAllotTeachers,
  canViewAllocations,
  isLoading,
  isNarrowed,
  busyUuid,
  onPageChange,
  onLimitChange,
  onClearFilters,
  onAddClass,
  onEdit,
  onDelete,
  onDeleteSection,
  onDuplicate,
  onAssignTeacher,
  onViewSubjectTeachers,
  onSetMonitor,
}: ClassesOverviewTableProps) {
  const navigate = useNavigate();
  /** One row open at a time — each open reads the allotments afresh. */
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-3">
      {/* Table Container with Sticky Class and Actions and visible scrollbar */}
      <div className="relative overflow-x-auto table-scrollbar rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111c2e] shadow-xs">
        <table className="w-full text-left border-collapse min-w-[950px]">
          <thead>
            <tr className="bg-slate-100 dark:bg-[#0b1322] border-b border-slate-200 dark:border-slate-800 text-[14px]  font-bold tracking-wide text-slate-700 dark:text-slate-300">
              <th className="py-3.5 px-4 sm:sticky sm:left-0 z-20 bg-slate-100 dark:bg-[#0b1322] sm:border-r border-slate-200 dark:border-slate-800 sm:shadow-2xs">
                Class
              </th>
              <th className="py-3.5 px-4">Subjects</th>
              <th className="py-3.5 px-4 text-right">Fee (Annual)</th>
              <th className="py-3.5 px-4">Class Teacher</th>
              <th className="py-3.5 px-4 text-right sm:sticky sm:right-0 z-20 bg-slate-100 dark:bg-[#0b1322] sm:border-l border-slate-200 dark:border-slate-800 sm:shadow-2xs">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200/80 dark:divide-slate-800/80 text-sm">
            {isLoading &&
              Array.from({ length: 5 }, (_, index) => (
                <tr key={`skel-${index}`}>
                  <td className="sm:sticky sm:left-0 z-10 bg-white dark:bg-[#111c2e] py-3.5 px-4 sm:border-r border-slate-200/60 dark:border-slate-800/60 sm:shadow-2xs">
                    <div className="h-4 w-24 bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
                    <div className="h-4 w-32 mt-1.5 bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
                    <div className="h-3 w-14 mt-1.5 bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
                  </td>
                  <td className="py-3 px-4">
                    <div className="h-4 w-48 bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
                    <div className="h-4 w-36 mt-1.5 bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="h-4 w-16 ml-auto bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
                  </td>
                  <td className="py-3 px-4">
                    <div className="h-4 w-32 bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
                  </td>
                  <td className="sm:sticky sm:right-0 z-10 bg-white dark:bg-[#111c2e] py-3 px-4 text-right sm:border-l border-slate-200/60 dark:border-slate-800/60 sm:shadow-2xs">
                    <div className="h-7 w-16 ml-auto bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
                  </td>
                </tr>
              ))}

            {!isLoading &&
              rows.map((sectionRow) => {
                const { instituteClass: row, section, classTeacher } = sectionRow;
                const stage = stageOfClass(row);
                const isExpanded = expandedKey === sectionRow.key;
                /** "9th - B", as the rest of the product names a section. */
                const label = section ? `${row.name} - ${section.name}` : row.name;
                // A row is one section; a class with none yet falls back to its own totals.
                const seats = section ? section.maxSeats : row.totalCapacity;
                const filled = section ? (section.studentCount ?? 0) : row.studentCount;
                return (
                  <Fragment key={sectionRow.key}>
                  <tr
                    className="hover:bg-slate-50/80 dark:hover:bg-slate-900/40 transition-colors"
                  >
                    {/* STICKY FIXED CLASS CELL */}
                    <td className="sm:sticky sm:left-0 z-10 bg-white dark:bg-[#111c2e] py-3 px-4 sm:border-r border-slate-200/60 dark:border-slate-800/60 sm:shadow-2xs whitespace-nowrap">
                      <div className="flex min-w-0 flex-col items-start gap-1">
                        <span className="flex items-center gap-1.5">
                          <span className="truncate text-[13.5px] font-extrabold text-slate-800 dark:text-slate-100">{classDisplayName(row.name)}</span>
                          {row.code && <span className="shrink-0 text-[11px] font-semibold text-slate-400">({row.code})</span>}
                        </span>
                        {/* The section this row is, beside the stage of school it sits in. */}
                        <span className="flex flex-wrap items-center gap-1.5">
                          {section ? (
                            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-md text-[11.5px] font-bold bg-slate-100 dark:bg-slate-800/80 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700">
                              Section {section.name}
                            </span>
                          ) : (
                            <span className="text-[11px] font-semibold text-slate-400">No sections yet</span>
                          )}
                          <span
                            className={cn(
                              'inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-extrabold tracking-tight',
                              STAGE_TONE[stage].badge,
                            )}
                          >
                            {stage}
                          </span>
                        </span>
                        {/* Seats sit under the section they belong to. The register
                            filters by class, so the link opens the whole class. */}
                        <Link
                          to={`${STUDENTS_PATH}?classUuid=${row.uuid}`}
                          aria-label={`${filled} of ${seats} seats filled — see the students in ${row.name}`}
                          className="inline-flex items-baseline gap-1 rounded-md px-1 text-[11.5px] font-semibold text-slate-500 dark:text-slate-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 transition-colors"
                        >
                          <span
                            className={cn(
                              'font-extrabold tabular-nums',
                              // A full section is the one admissions must stop placing into.
                              filled >= seats && seats > 0
                                ? 'text-rose-600 dark:text-rose-400'
                                : 'text-indigo-600 dark:text-indigo-400',
                            )}
                          >
                            {filled}
                          </span>
                          <span className="tabular-nums">/ {seats}</span>
                          seats filled
                        </Link>
                        {section && (
                          <span className="flex items-center gap-1.5 px-1 text-[11.5px] font-semibold text-slate-500 dark:text-slate-400">
                            <UserCheck className="h-3.5 w-3.5 shrink-0" aria-hidden />
                            {section.monitor ? (
                              <span className="truncate">
                                Monitor:{' '}
                                <span className="font-extrabold text-slate-700 dark:text-slate-200">
                                  {section.monitor.fullName}
                                </span>
                              </span>
                            ) : (
                              <span>No monitor</span>
                            )}
                            {onSetMonitor && (
                              <button
                                type="button"
                                onClick={() => onSetMonitor(row, section)}
                                aria-label={`${section.monitor ? 'Change' : 'Assign'} the class monitor of ${label}`}
                                className="rounded-md px-1 font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                              >
                                {section.monitor ? 'Change' : 'Assign'}
                              </button>
                            )}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* MIDDLE SCROLLABLE CELLS */}
                    <td className="py-2.5 px-4 align-middle">
                      <SubjectsCell instituteClass={row} />
                    </td>

                    <td className="py-2 px-4 text-right whitespace-nowrap">
                      <span className="text-[13px] font-extrabold tabular-nums text-slate-700 dark:text-slate-200">
                        {formatCurrency(row.totalFee)}
                      </span>
                    </td>

                    <td className="py-2 px-4 whitespace-nowrap">
                      <div className="flex flex-col items-start gap-1">
                        <TeacherCell teacher={classTeacher} />
                        {canViewAllocations && (
                          <button
                            type="button"
                            onClick={() => setExpandedKey(isExpanded ? null : sectionRow.key)}
                            aria-expanded={isExpanded}
                            aria-controls={`teaching-${sectionRow.key}`}
                            className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-[11px] font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                          >
                            {isExpanded ? 'Hide assigned teachers' : 'View assigned teachers'}
                            <ChevronDown
                              className={cn('h-3.5 w-3.5 transition-transform', isExpanded && 'rotate-180')}
                              aria-hidden
                            />
                          </button>
                        )}
                      </div>
                    </td>

                    {/* STICKY FIXED ACTIONS CELL */}
                    <td className="sm:sticky sm:right-0 z-10 bg-white dark:bg-[#111c2e] py-2.5 px-4 text-right whitespace-nowrap sm:border-l border-slate-200/60 dark:border-slate-800/60 sm:shadow-2xs">
                      <div className="flex items-center justify-end gap-1">
                        {canManage && (
                          <>
                            <button
                              type="button"
                              title="Edit class"
                              onClick={() => onEdit(row)}
                              className="p-1.5 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 transition-colors cursor-pointer rounded-lg border-0 bg-transparent"
                            >
                              <PencilLine className="h-4 w-4" />
                            </button>

                            <button
                              type="button"
                              title={
                                section && row.sections.length > 1
                                  ? `Suspend ${label} — ${row.name}'s other sections stay`
                                  : `Suspend ${row.name}`
                              }
                              aria-label={
                                section && row.sections.length > 1 ? `Suspend ${label}` : `Suspend ${row.name}`
                              }
                              // A row is one section: its bin suspends that section. Only a
                              // class down to its last section is suspended as a whole.
                              onClick={() =>
                                section && row.sections.length > 1 ? onDeleteSection(row, section) : onDelete(row)
                              }
                              className="p-1.5 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/60 transition-colors cursor-pointer rounded-lg border-0 bg-transparent"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}

                        <Menu
                          label={`More actions for ${label}`}
                          triggerClassName="p-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer rounded-lg border-0 bg-transparent shadow-none"
                          trigger={<MoreVertical className="h-4 w-4" aria-hidden />}
                          items={[
                            {
                              key: 'students',
                              label: 'View students',
                              icon: <Users className="h-4 w-4" />,
                              onSelect: () => navigate(`${STUDENTS_PATH}?classUuid=${row.uuid}`),
                            },
                            {
                              key: 'sections',
                              label: 'Manage sections',
                              icon: <Layers className="h-4 w-4" />,
                              disabled: !canManage,
                              onSelect: () => onEdit(row),
                            },
                            {
                              key: 'subjectTeachers',
                              label: 'Subject teachers',
                              description: 'View assigned teachers section by section',
                              icon: <BookOpen className="h-4 w-4" />,
                              disabled: !canViewAllocations,
                              onSelect: () => onViewSubjectTeachers(row),
                            },
                            {
                              key: 'allot',
                              label: 'Assign class teacher',
                              description: 'One per section, without leaving this screen',
                              icon: <UserPlus className="h-4 w-4" />,
                              disabled: !canAllotTeachers,
                              onSelect: () => onAssignTeacher(row),
                            },
                            {
                              key: 'duplicate',
                              label: 'Duplicate class',
                              description: 'Same sections, subjects and fee',
                              icon: <CopyPlus className="h-4 w-4" />,
                              disabled: !canManage || busyUuid !== null,
                              onSelect: () => onDuplicate(row),
                            },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr id={`teaching-${sectionRow.key}`}>
                      {/* Pinned to the left edge and capped, so the grid stays in
                          view however far the wide roster is scrolled. */}
                      <td
                        colSpan={5}
                        className="border-l-4 border-indigo-500 bg-slate-50 dark:bg-[#0b1322] p-0"
                      >
                        <div className="sm:sticky sm:left-0 w-full max-w-6xl p-4 sm:p-5">
                          <div className="mb-4 flex items-center gap-2.5">
                            <span
                              className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400"
                              aria-hidden
                            >
                              <Users className="h-4 w-4" />
                            </span>
                            <div>
                              <p className="text-base font-black text-slate-900 dark:text-white">
                                {label} — Assigned Teachers
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                Class teacher and subject teachers assigned to this section.
                              </p>
                            </div>
                          </div>
                          <ClassTeachingGrid
                            classUuid={row.uuid}
                            className={row.name}
                            sectionUuid={section?.uuid}
                            // Re-read once the page reloads after a class teacher is assigned.
                            reloadKey={row.classTeachers.map((one) => `${one.sectionName}:${one.uuid}`).join('|')}
                            canEdit={canAllotTeachers}
                            onAssignClassTeacher={canAllotTeachers ? () => onAssignTeacher(row) : undefined}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}

            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="py-8">
                  {isNarrowed ? (
                    <EmptyState
                      icon={<Search className="h-5 w-5" />}
                      title="Nothing matches those filters"
                      description="No class on the roster matches what you have narrowed it to."
                      action={
                        <Button variant="secondary" onClick={onClearFilters}>
                          Clear filters
                        </Button>
                      }
                    />
                  ) : (
                    <EmptyState
                      icon={<UserCheck className="h-5 w-5" />}
                      title="No classes yet"
                      description={
                        canManage
                          ? 'Most institutes start with the youngest class and work upwards.'
                          : 'Nobody has set up a class yet. Your institute administrator can add the first one.'
                      }
                      action={
                        canManage ? <Button onClick={onAddClass}>Add your first class</Button> : undefined
                      }
                    />
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        meta={meta}
        itemLabel="sections"
        showPageNumbers
        limitOptions={ROWS_PER_PAGE}
        onLimitChange={onLimitChange}
        onPageChange={onPageChange}
      />
    </div>
  );
}
